import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback, memo } from 'react';

import TextBox from 'devextreme-react/text-box';
import { Button } from 'devextreme-react/button';
import Box, { Item as BoxItem } from 'devextreme-react/box';
import { unpack, pack } from 'msgpackr';
import stringify from '../utils/stableStringify';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { Template } from 'devextreme-react/core/template';
import DropDownButton from 'devextreme-react/drop-down-button';
import AntLocalization from '../utils/AntLocalization';

import AntErrorPanel from './AntErrorPanel';
import AntGridDateFilter from './AntGridDateFilter';
import AntGridColumnChooser from './AntGridColumnChooser';

const renderColumnFilterIcon = () => (
    <i className="dx-icon ant-grid-column-filter-icon">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <path d="M3 6h18M7 12h10M10 18h4" />
        </svg>
    </i>
);

// Nút bật/tắt dòng lọc trên cột: tự giữ trạng thái sáng (AntGrid giữ trạng thái thật trong ref, không render lại).
const ColumnFilterToggle = ({ active, onToggle, focusStateEnabled }) => {
    const [on, setOn] = React.useState(Boolean(active));
    return (
        <div className={'ant-grid-column-filter-toggle' + (on ? ' ant-grid-column-filter-toggle-active' : '')}>
            <Button
                // Icon nét mảnh "list-filter" cùng cỡ/màu các icon thanh công cụ (phễu đặc của DevExtreme quá nặng).
                render={renderColumnFilterIcon}
                stylingMode="text"
                focusStateEnabled={focusStateEnabled}
                hint={
                    on
                        ? AntLocalization.translateText({ vi: 'Ẩn dòng lọc (bỏ lọc trên cột)', en: 'Hide filter row (clear column filters)', fr: 'Masquer la ligne de filtre (effacer)', es: 'Ocultar fila de filtro (borrar filtros)', id: 'Sembunyikan baris filter (hapus filter)', ja: 'フィルター行を隠す（列フィルター解除）', zh: '隐藏筛选行（清除列筛选）', ko: '필터 행 숨기기(열 필터 해제)', hi: 'फ़िल्टर पंक्ति छिपाएँ (फ़िल्टर हटाएँ)', ar: 'إخفاء صف التصفية (مسح عوامل التصفية)' })
                        : AntLocalization.translateText({ vi: 'Lọc trên từng cột', en: 'Filter by column', fr: 'Filtrer par colonne', es: 'Filtrar por columna', id: 'Filter per kolom', ja: '列ごとにフィルター', zh: '按列筛选', ko: '열별 필터', hi: 'कॉलम अनुसार फ़िल्टर', ar: 'تصفية حسب العمود' })
                }
                onClick={() => {
                    const next = onToggle ? onToggle() : !on;
                    setOn(Boolean(next));
                }}
            />
        </div>
    );
};
export default memo(forwardRef(function (props, ref) {
    const [searchText, setSearchText] = React.useState(props.searchText ? props.searchText : null);
    const searchTextFocused = useRef(false);

    const searchTextComp = useRef();
    const searchTextTimeOut = useRef();
    const previousSearchProp = useRef(props.searchText);
    const barcodeFocusing = useRef(false);
    const barcodeFocusTimer = useRef();

    const [filterBySelected, setFilterBySelected] = React.useState(props.filterBySelected !== undefined ? props.filterBySelected : false);
    const showSearchBar = !filterBySelected && (props.showSearchBar !== undefined ? props.showSearchBar : false);
    const showScanBarcode = !filterBySelected && (props.showScanBarcode !== undefined ? props.showScanBarcode : false);

    // const [showScanBarcodeBar, setShowScanBarcodeBar] = React.useState(false);
    const [showScanBarcodeBar, setShowScanBarcodeBar] = React.useState(true);

    const [selectedAll, setSelectedAll] = React.useState(false);
    const [countValues, setCountValues] = React.useState([]);

    const [scanBarcodeValue, setScanBarcodeValue] = React.useState('');
    const [scanBarcodeError, setScanBarcodeError] = React.useState('');
    const scanBarcodeComp = useRef();

    const [customOpts, setCustomOpts] = React.useState(null);
    const contextualBatchActions = customOpts?.contextualBatchActions instanceof Array ? customOpts.contextualBatchActions : [];
    const batchActionItems = useMemo(
        () =>
            contextualBatchActions.map((action) => {
                const actionName = action.code || action.name;
                const actionText = AntLocalization.translateText(action.text || action.label || actionName);
                const unavailableText =
                    action.maxSelection && action.selectedCount > action.maxSelection
                        ? `${AntLocalization.translateText({ vi: 'Tối đa', en: 'Maximum', fr: 'Maximum', es: 'Máximo', id: 'Maksimum', ja: '最大', zh: '最多', ko: '최대', hi: 'अधिकतम', ar: 'بحد أقصى' })} ${action.maxSelection} ${AntLocalization.translateText({
                              vi: 'dòng',
                              en: 'rows',
                              fr: 'lignes',
                              es: 'filas',
                              id: 'baris',
                              ja: '行',
                              zh: '行',
                              ko: '행',
                              hi: 'पंक्ति',
                              ar: 'صفوف'
                          })}`
                        : `${action.eligibleCount}/${action.selectedCount} ${AntLocalization.translateText({
                              vi: 'dòng đủ điều kiện',
                              en: 'eligible rows',
                              fr: 'lignes éligibles',
                              es: 'filas aptas',
                              id: 'baris memenuhi syarat',
                              ja: '行が対象',
                              zh: '行符合条件',
                              ko: '행 해당',
                              hi: 'पात्र पंक्तियाँ',
                              ar: 'صفوف مؤهلة'
                          })}`;
                return {
                    ...action,
                    id: actionName,
                    text: actionText,
                    disabled: !action.enabled,
                    hint: action.enabled ? actionText : `${actionText} — ${unavailableText}`
                };
            }),
        [contextualBatchActions]
    );

    const initFilteredGroups = useCallback(() => {
        if (props.items instanceof Array) {
            var item = props.items.find((i) => i.widget === 'ButtonGroup');
            if (item && item.items instanceof Array && item.items.length > 0) {
                if (props.filteredGroup) {
                    var found = item.items.find((i) => i.name === props.filteredGroup);
                    if (found) return [found];
                }
                if (item.defaultSelect) {
                    var found = item.items.find((i) => i.name === item.defaultSelect);
                    if (found) return [found];
                }
                if (item && item.items instanceof Array) {
                    var found = item.items.find((i) => i.default === true);
                    if (found) return [found];
                }
                if (item.items.length > 0) {
                    return [item.items[0]];
                }
            }
        }

        return [];
    }, [props.items, props.filteredGroup]);

    const [filteredByGroups, setFilteredByGroups] = React.useState(initFilteredGroups());

    const initMainItemsValue = useCallback(() => {
        let obj = {};

        if (props.showActions !== false && props.items instanceof Array) {
            for (let i = 0; i < props.items.length; i++) {
                var item = props.items[i];
                if (item.name !== undefined && item.widget === 'CheckBox' && (item.searchable !== undefined ? item.searchable : true)) {
                    if (item.defaultValue !== undefined) obj[item.name] = item.defaultValue;
                    else obj[item.name] = false;
                }
            }
        }
        return obj;
    }, [props.items, props.showActions]);

    const [mainItemsValue, setMainItemsValue] = React.useState(initMainItemsValue());

    const focusInSearchBar = useCallback(() => {
        var autoFocus = props.autoFocusSearchBar !== undefined ? props.autoFocusSearchBar : true;
        if (autoFocus && searchTextComp.current) searchTextComp.current.focus();
    }, [props.autoFocusSearchBar]);

    const searchFocusTimer = useRef();
    const latestSearchCallback = useRef(props.onSearchText);
    latestSearchCallback.current = props.onSearchText;
    const onSearchValueChanged = useCallback((e) => setSearchText(e.value), []);
    const onSearchInitialized = useCallback((e) => {
        searchTextComp.current = e.component;
        if (props.autoFocusSearchBar !== false) {
            clearTimeout(searchFocusTimer.current);
            searchFocusTimer.current = setTimeout(focusInSearchBar);
        }
    }, [props.autoFocusSearchBar, focusInSearchBar]);
    const onSearchFocusIn = useCallback((e) => {
        searchTextFocused.current = true;
        if (props.onStartSearchText) props.onStartSearchText(e);
    }, [props.onStartSearchText]);
    const onSearchFocusOut = useCallback((e) => {
        searchTextFocused.current = false;
        if (props.onEndSearchText) props.onEndSearchText(e);
    }, [props.onEndSearchText]);
    const onSearchDisposing = useCallback(() => {
        clearTimeout(searchFocusTimer.current);
        searchTextComp.current = null;
        searchTextFocused.current = false;
    }, []);

    const intDefaultOpts = useCallback(
        (_customOpts, _prevOpts) => {
            var obj = {
                ..._prevOpts,
                enableEdit: _customOpts ? _customOpts.enableEdit : false,
                enableDelete: _customOpts ? _customOpts.enableDelete : false,
                showRefresh: props.showRefresh !== undefined ? props.showRefresh : true,
                showScheduler: props.showScheduler !== undefined ? props.showScheduler : false,
                showCardView: props.showCardView !== undefined ? props.showCardView : false,
                showChart: props.showChart !== undefined ? props.showChart : false,
                showSelectAll: props.showSelectAll !== undefined ? props.showSelectAll : false,
                showEdit: props.showEdit !== undefined ? props.showEdit : false,
                showDelete: props.showDelete !== undefined ? props.showDelete : false,
                showAdd: props.showAdd !== undefined ? props.showAdd : false,
                showFilterBySelected: props.showFilterBySelected !== undefined ? props.showFilterBySelected : false,
                showFilter: props.showFilter !== undefined ? props.showFilter : false,
                showImport: props.showImport !== undefined ? props.showImport : false,
                showDownload: props.showDownload !== undefined ? props.showDownload : false,

                searchBarWidth: props.searchBarWidth !== undefined ? props.searchBarWidth : 154,
                scanBarcodeWidth: props.scanBarcodeWidth !== undefined ? props.scanBarcodeWidth : 154,

                showFieldChooser: props.showFieldChooser !== undefined ? props.showFieldChooser : true,
                locationRefresh: props.locationRefresh !== undefined ? props.locationRefresh : 'before',
                locationChart: props.locationChart !== undefined ? props.locationChart : 'before',
                locationScheduler: props.locationScheduler !== undefined ? props.locationScheduler : 'after',
                locationCardView: props.locationCardView !== undefined ? props.locationCardView : 'after',
                locationSelectAll: props.locationSelectAll !== undefined ? props.locationSelectAll : 'before',
                locationEdit: props.locationEdit !== undefined ? props.locationEdit : 'before',
                locationDelete: props.locationDelete !== undefined ? props.locationDelete : 'before',
                locationAdd: props.locationAdd !== undefined ? props.locationAdd : 'before',
                locationFilter: props.locationFilter !== undefined ? props.locationFilter : 'after',
                locationFilterBySelected: props.locationFilterBySelected !== undefined ? props.locationFilterBySelected : 'after',
                locationImport: props.locationImport !== undefined ? props.locationImport : 'before',
                locationDownload: props.locationDownload !== undefined ? props.locationDownload : 'after',
                locationSearchBar: props.locationSearchBar !== undefined ? props.locationSearchBar : 'after',
                locationScanBarcode: props.locationScanBarcode !== undefined ? props.locationScanBarcode : 'before'
            };

            return obj;
        },
        [
            props.showRefresh,
            props.showScheduler,
            props.showCardView,
            props.showChart,
            props.showSelectAll,
            props.showEdit,
            props.showDelete,
            props.showAdd,
            props.showFilterBySelected,
            props.showFilter,
            props.showImport,
            props.showDownload,
            props.searchBarWidth,
            props.scanBarcodeWidth,
            props.showFieldChooser,
            props.locationRefresh,
            props.locationChart,
            props.locationScheduler,
            props.locationCardView,
            props.locationSelectAll,
            props.locationEdit,
            props.locationDelete,
            props.locationAdd,
            props.locationFilter,
            props.locationFilterBySelected,
            props.locationImport,
            props.locationDownload,
            props.locationSearchBar,
            props.locationScanBarcode
        ]
    );

    const prevOptsRef = useRef();

    const opts = useMemo(() => {
        const prevOpts = prevOptsRef.current;
        const newOpts = intDefaultOpts(customOpts, prevOpts);
        prevOptsRef.current = newOpts;
        return newOpts;
    }, [intDefaultOpts, customOpts]);

    const switchBarcodeButton = useCallback(() => {
        setShowScanBarcodeBar(!showScanBarcodeBar);
        if (showScanBarcodeBar) barcodeFocusing.current = false;
    }, [showScanBarcodeBar]);

    const onScanBarcode = useCallback(
        async (e) => {
            barcodeFocusing.current = true;

            if (props.onScanBarcode) {
                let result = await props.onScanBarcode(e.value);
                if (result?.error) {
                    setScanBarcodeError(result?.error);
                    return;
                }
            }

            setScanBarcodeValue('');
            setScanBarcodeError('');

            if (e.component) {
                scanBarcodeComp.current = e.component;

                // setTimeout(
                //     (e) => {
                //         e.component.focus();
                //     },
                //     300,
                //     e
                // );
            }
        },
        [props.onScanBarcode]
    );

    const onScanBarcodeReady = useCallback(
        (args) => {
            scanBarcodeComp.current = args.component;
            clearTimeout(barcodeFocusTimer.current);

            var autoFocus = props.autoFocusSearchBarcode !== undefined ? props.autoFocusSearchBarcode : true;

            // autoFocus = Boolean(barcodeFocusing.current);

            if (autoFocus && showScanBarcode && showScanBarcodeBar && !searchTextFocused.current) {
                args.component.focus();
                barcodeFocusTimer.current = setTimeout(() => {
                    if (!searchTextFocused.current && scanBarcodeComp.current === args.component) args.component.focus();
                }, 200);
            }
        },
        [props.autoFocusSearchBarcode, showScanBarcode, showScanBarcodeBar]
    );

    const initBarItems = useCallback(() => {
        var items = [];

        if (opts.showRefresh)
            items.push({
                name: 'refresh',
                location: opts.locationRefresh,
                locateInMenu: props.showMenu !== false ? 'auto' : 'never',
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    focusStateEnabled: props.focusStateEnabled,
                    stylingMode: 'text',
                    onClick: () => {
                        if (props.onRefreshClick) props.onRefreshClick();
                    }
                }
            });

        if (opts.showChart)
            items.push({
                name: 'refresh',
                location: opts.locationChart,
                locateInMenu: props.showMenu !== false ? 'auto' : 'never',
                widget: 'dxButton',
                options: {
                    icon: 'chart',
                    focusStateEnabled: props.focusStateEnabled,
                    stylingMode: 'text',
                    onClick: () => {
                        if (props.onShowChart) props.onShowChart();
                    }
                }
            });
        // if (opts.showSelectAll && !filterBySelected)
        //     items.push({
        //         name: 'selectAll',
        //         location: 'after', // opts.locationSelectAll,
        //         widget: 'dxCheckBox',
        //         locateInMenu: props.showMenu !== false ? 'auto' : 'never',
        //         options: {
        //             stylingMode: 'text',
        //             text: 'Chọn hết',
        //             width: 120,
        //             focusStateEnabled: props.focusStateEnabled,
        //             value: Boolean(selectedAll),
        //             onValueChanged: (e) => {
        //                 setSelectedAll(e.value);

        //                 if (props.onAction) props.onAction({ name: 'selectAll', value: e.value });
        //                 if (props.onSelectAll) props.onSelectAll(e.value);
        //             }
        //         }
        //     });

        if (opts.showEdit)
            items.push({
                name: 'edit',
                location: opts.locationEdit,
                widget: 'dxButton',
                locateInMenu: props.showMenu !== false ? 'auto' : 'never',
                options: {
                    icon: 'edit',
                    disabled: !opts.enableEdit,
                    focusStateEnabled: props.focusStateEnabled,
                    stylingMode: 'text',
                    onClick: () => {
                        if (props.onEditClick) props.onEditClick();
                    }
                }
            });

        if (opts.showDelete)
            items.push({
                name: 'delete',
                location: opts.locationDelete,
                widget: 'dxButton',
                locateInMenu: props.showMenu !== false ? 'auto' : 'never',
                options: {
                    icon: 'trash',
                    disabled: !opts.enableDelete,
                    focusStateEnabled: props.focusStateEnabled,
                    stylingMode: 'text',
                    onClick: () => {
                        if (props.onDeleteClick) props.onDeleteClick();
                    }
                }
            });

        if (opts.showImport)
            items.push({
                name: 'import',
                location: opts.locationImport,
                widget: 'dxButton',
                locateInMenu: props.showMenu !== false ? 'auto' : 'never',
                options: {
                    icon: 'parentfolder',
                    focusStateEnabled: props.focusStateEnabled,
                    stylingMode: 'text',
                    onClick: () => {
                        if (props.onImportClick) props.onImportClick();
                    }
                }
            });

        if (opts.showAdd)
            items.push({
                name: 'add',
                location: opts.locationAdd,
                widget: 'dxButton',
                locateInMenu: props.showMenu !== false ? 'auto' : 'never',
                options: {
                    icon: 'plus',
                    stylingMode: 'text',
                    text: AntLocalization.translateText({ vi: 'THÊM', en: 'ADD', fr: 'AJOUTER', es: 'AÑADIR', id: 'TAMBAH', ja: '追加', zh: '添加', ko: '추가', hi: 'जोड़ें', ar: 'إضافة' }),
                    focusStateEnabled: props.focusStateEnabled,
                    onClick: () => {
                        if (props.onAddClick) props.onAddClick();
                    }
                }
            });

        // AntGrid báo hasSelectedRows qua resetItems; checkbox chỉ hiện khi có dòng được chọn.
        const filterBySelectedVisible =
            opts.showFilterBySelected && (!props.showFilterBySelectedOnlyWhenSelected || Boolean(customOpts && customOpts.hasSelectedRows));
        if (filterBySelectedVisible)
            items.push({
                name: 'filterBySelected',
                location: opts.locationFilterBySelected,
                widget: 'dxCheckBox',
                locateInMenu: props.showMenu !== false ? 'auto' : 'never',
                options: {
                    stylingMode: 'text',
                    text: AntLocalization.translateText({ vi: 'Đã chọn', en: 'Selected', fr: 'Sélectionné', es: 'Seleccionado', id: 'Terpilih', ja: '選択済み', zh: '已选择', ko: '선택됨', hi: 'चयनित', ar: 'محدد' }),
                    width: 90,
                    value: Boolean(filterBySelected),
                    focusStateEnabled: props.focusStateEnabled,
                    onValueChanged: (e) => {
                        setFilterBySelected(e.value);
                    }
                }
            });

        if (props.showActions !== false && props.items instanceof Array) {
            for (let i = 0; i < props.items.length; i++) {
                let item = props.items[i];

                item = JSON.parse(stringify(item));
                item.text = AntLocalization.translateText(item.text);

                if (item.widget === 'CheckBox')
                    if (!opts.filterBySelected)
                        items.push({
                            name: item.name,
                            searchable: item.name !== undefined && (item.searchable !== undefined ? item.searchable : true),
                            location: props.items.some((i) => i.widget === 'ButtonGroup') ? 'after' : item.location ? item.location : 'before',
                            locateInMenu: props.showMenu !== false ? (item.locateInMenu ? item.locateInMenu : 'auto') : 'never',
                            widget: 'dxCheckBox',
                            options: {
                                focusStateEnabled: props.focusStateEnabled,
                                icon: item.icon,
                                text: item.text,
                                width: item.width,
                                value:
                                    item.name && mainItemsValue[item.name] !== undefined
                                        ? Boolean(mainItemsValue[item.name])
                                        : item.defaultValue !== undefined
                                            ? item.defaultValue
                                            : false,
                                stylingMode: 'text',
                                onValueChanged: (e) => {
                                    if (item.name) setMainItemsValue((v) => Object.assign(v, { [item.name]: e.value }));

                                    var value = e.value;
                                    if (item.values instanceof Array) {
                                        var found = item.values.find((v) => v.value === e.value && v.postValue !== undefined);
                                        if (found) value = found.postValue;
                                    }

                                    if (item.onValueChanged)
                                        item.onValueChanged({
                                            name: item.name,
                                            text: item.text,
                                            value: value
                                        });
                                    if (props.onAction) {
                                        props.onAction({
                                            name: item.name,
                                            text: item.text,
                                            value: value
                                        });
                                    }
                                }
                            }
                        });

                if (item.widget === 'Button') {
                    let isActionVisible = true;

                    if (item.equivalentParentActions instanceof Array) {
                        isActionVisible = false;
                        if (props.parentData?.actions?.some((a) => item.equivalentParentActions.includes(a.name) && a.visible)) {
                            isActionVisible = true;
                        }
                    }

                    if (isActionVisible)
                        items.push({
                            name: item.name,
                            location: props.items.some((i) => i.widget === 'ButtonGroup') ? 'after' : item.location ? item.location : 'after',

                            locateInMenu: props.showMenu !== false ? (item.locateInMenu ? item.locateInMenu : 'auto') : 'never',
                            widget: 'dxButton',
                            options: {
                                focusStateEnabled: props.focusStateEnabled,
                                icon: item.icon,
                                text: item.text
                                    ? AntLocalization.translateText(item.text)
                                    : item.icon === 'plus'
                                        ? AntLocalization.translateText({ vi: 'THÊM', en: 'ADD', fr: 'AJOUTER', es: 'AÑADIR', id: 'TAMBAH', ja: '追加', zh: '添加', ko: '추가', hi: 'जोड़ें', ar: 'إضافة' })
                                        : undefined,
                                width: item.width,
                                stylingMode: item.stylingMode ? item.stylingMode : 'text',
                                onClick: () => {
                                    if (props.onAction) props.onAction({ name: item.name, text: item.text });
                                }
                            },
                            equivalentActions: item.equivalentActions
                        });
                }

                if (item.widget === 'ButtonGroup') {
                    // var selectedGr = props.filteredGroup ? props.filteredGroup : item.defaultSelect;
                    if (item.items instanceof Array)
                        item.items = item.items.map((c) => {
                            c.text = AntLocalization.translateText(c.text);
                            let found = countValues.find((v) => v.name === c.name);
                            c.countValue = found ? found.value : undefined;
                            return c;
                        });

                    items.push({
                        name: item.name,
                        location: item.location ? item.location : 'after',
                        locateInMenu: props.showMenu !== false ? (item.locateInMenu ? item.locateInMenu : 'auto') : 'never',
                        widget: 'dxButtonGroup',
                        options: {
                            items: item.items,
                            selectedItemKeys:
                                filteredByGroups.length > 0
                                    ? filteredByGroups.map((g) => g.name)
                                    : item.defaultSelect instanceof Array
                                        ? [item.defaultSelect]
                                        : item.selectedItemKeys instanceof Array
                                            ? item.selectedItemKeys
                                            : [],
                            keyExpr: item.keyExpr ? item.keyExpr : 'name',
                            selectionMode: item.selectionMode ? item.selectionMode : 'single',
                            focusStateEnabled: props.focusStateEnabled,
                            icon: item.icon,
                            text: item.text,
                            width: item.width,
                            buttonTemplate: item.widget === 'ButtonGroup' ? 'renderButtonOnGroup' : undefined,
                            stylingMode: item.stylingMode ? item.stylingMode : 'text',
                            // onItemClick: (e) => {
                            //     if (props.onAction)
                            //         props.onAction({
                            //             name: item.name,
                            //             item: item.items[e.itemIndex],
                            //             text: item.text
                            //         });
                            // },

                            onSelectionChanged: (e) => {
                                setFilteredByGroups(e.addedItems);
                                if (props.onFilteredByGroup) {
                                    if (e.addedItems.length > 0) {
                                        let gr = e.addedItems[0];

                                        props.onFilteredByGroup(gr);
                                    }
                                }
                            }
                        }
                    });
                }

                if (item.widget === 'Menu')
                    items.push({
                        name: item.name,
                        location: item.location ? item.location : 'after',
                        locateInMenu: props.showMenu !== false ? (item.locateInMenu ? item.locateInMenu : 'auto') : 'never',
                        widget: 'dxMenu',
                        options: {
                            items: item.items,
                            valueExpr: item.valueExpr,
                            displayExpr: item.displayExpr,
                            focusStateEnabled: props.focusStateEnabled,
                            icon: item.icon,
                            text: item.text,
                            width: item.width,
                            stylingMode: item.stylingMode ? item.stylingMode : 'text',
                            onItemClick: (e) => {
                                if (props.onAction)
                                    props.onAction({
                                        name: item.name,
                                        item: item.items[e.itemIndex],
                                        text: item.text
                                    });
                            }
                        }
                    });
            }
        }

        // if (showScanBarcode) {
        // if (!showScanBarcodeBar)
        items.push({
            name: 'showBarcode',
            location: opts.locationScanBarcode,
            widget: 'dxButton',
            visible: Boolean(showScanBarcode && !showScanBarcodeBar),
            locateInMenu: props.showMenu !== false ? 'auto' : 'never',
            options: {
                icon: 'fa fa-barcode',
                stylingMode: 'text',
                focusStateEnabled: props.focusStateEnabled,
                onClick: switchBarcodeButton
            }
        });

        // if (showScanBarcodeBar)
        items.push({
            name: 'scanBarcode',
            cssClass: 'ant-barcode-scanner',
            searchable: true,
            visible: Boolean(showScanBarcode && showScanBarcodeBar),
            location: opts.locationScanBarcode,
            locateInMenu: props.showMenu !== false ? 'auto' : 'never',
            widget: 'dxTextBox',
            options: {
                buttons: [
                    {
                        name: 'searchIcon',
                        location: 'before',
                        options: {
                            icon: 'fa fa-barcode',
                            type: 'normal',
                            stylingMode: 'text',
                            // disabled: true,
                            onClick: switchBarcodeButton
                        }
                    },
                    'clear'
                ],
                showClearButton: true,
                mode: 'search',
                placeholder: AntLocalization.translateText({ vi: 'Quét mã vạch...', en: 'Scan Barcode...', fr: 'Scanner le code...', es: 'Escanear código...', id: 'Pindai Barcode...', ja: 'バーコードをスキャン...', zh: '扫描条形码...', ko: '바코드 스캔...', hi: 'बारकोड स्कैन करें...', ar: 'مسح الباركود...' }),
                width: opts.scanBarcodeWidth,
                value: scanBarcodeValue,
                valueChangeEvent: 'change',
                onValueChanged: onScanBarcode,
                onFocusIn: (e) => {
                    barcodeFocusing.current = true;
                    // setScanBarcodeValue('');
                    // if (props.onStartScanBarcode) props.onStartScanBarcode(e);
                },
                // onFocusOut: (e) => {
                //     setScanBarcodeValue('');
                //     // if (props.onEndScanBarcode) props.onEndScanBarcode(e);
                // }
                onContentReady: onScanBarcodeReady
            }
        });
        // }

        if (opts.showScheduler)
            items.push({
                name: 'scheduler',
                location: opts.locationScheduler,
                locateInMenu: props.showMenu !== false ? 'auto' : 'never',
                widget: 'dxButton',
                options: {
                    icon: props.isSchedulerMode ? 'contentlayout' : 'event',
                    focusStateEnabled: props.focusStateEnabled,
                    stylingMode: 'text',
                    onClick: () => {
                        if (props.onShowScheduler) props.onShowScheduler();
                    }
                }
            });

        if (opts.showCardView)
            items.push({
                name: 'cardView',
                location: opts.locationCardView,
                locateInMenu: props.showMenu !== false ? 'auto' : 'never',
                widget: 'dxButton',
                options: {
                    icon: props.isCardviewMode ? 'contentlayout' : 'smalliconslayout',
                    focusStateEnabled: props.focusStateEnabled,
                    stylingMode: 'text',
                    onClick: () => {
                        if (props.onShowCardView) props.onShowCardView();
                    }
                }
            });

        // Nút download nằm ngoài toolbar, sát cụm lọc ngày + ô tìm kiếm (xem searchTools).

        // if (opts.showFieldChooser)
        //     items.push({
        //         name: 'fieldchooser',
        //         location: 'after',
        //         locateInMenu: 'never',
        //         widget: 'dxButton',
        //         options: {
        //             icon: 'fieldchooser',
        //             focusStateEnabled: props.focusStateEnabled,
        //             stylingMode: 'text',
        //             onClick: () => {
        //                 if (props.onShowFieldChooser) props.onShowFieldChooser();
        //             }
        //         }
        //     });

        if (opts.showFilter)
            items.push({
                name: 'filter',
                location: 'after',
                locateInMenu: 'never',
                widget: 'dxButton',
                options: {
                    icon: 'find',
                    focusStateEnabled: props.focusStateEnabled,
                    stylingMode: 'text',
                    onClick: props.onFilterClick
                }
            });

        return items;
    }, [
        scanBarcodeError,
        opts,
        countValues,
        scanBarcodeValue,
        onScanBarcode,
        switchBarcodeButton,
        onScanBarcodeReady,
        filterBySelected,
        filteredByGroups,
        mainItemsValue,
        showScanBarcode,
        showScanBarcodeBar,
        customOpts,
        props.parentData,
        props.showMenu,
        props.focusStateEnabled,
        props.onRefreshClick,
        props.onShowChart,
        props.onEditClick,
        props.onDeleteClick,
        props.onBatchAction,
        props.onImportClick,
        props.onAddClick,
        props.showActions,
        props.onAction,
        props.onFilteredByGroup,
        props.onStartScanBarcode,
        props.onEndScanBarcode,
        props.onScanBarcode,
        props.autoFocusSearchBarcode,
        props.onShowScheduler,
        props.onShowCardView,
        props.onDownloadClick,
        props.onFilterClick,
        props.showFilterBySelectedOnlyWhenSelected
    ]);

    const mainItems = useMemo(() => initBarItems(), [initBarItems]);

    const focusInScanBarcode = useCallback(() => {
        // console.log('ant-log-focusInScanBarcode-1 : ');
        var autoFocus = props.autoFocusSearchBarcode !== undefined ? props.autoFocusSearchBarcode : true;
        // console.log('ant-log-focusInScanBarcode-2-autoFocus : ', autoFocus);
        if (autoFocus && scanBarcodeComp.current) {
            // console.log('ant-log-focusInScanBarcode-3 : ');
            scanBarcodeComp.current.focus();
        }
    }, [props.autoFocusSearchBarcode]);

    const renderButtonOnGroup = useCallback((btn) => {
        return (
            <div
                style={{
                    verticalAlign: 'center',
                    display: 'inline-flex'
                }}
            >
                <span
                    className="dx-button-text"
                    style={{
                        fontWeight: 'bold',
                        color: 'var(--AnteTitleColor)'
                    }}
                >
                    {btn.text}
                </span>
                {/* <span className="dx-button-text"> {btn.countValue}</span> */}
                {btn.countValue > 0 && (
                    <span
                        className="dx-button-text ant-grid-bar-count"
                        style={{
                            marginLeft: '6px',
                            paddingLeft: '3px',
                            paddingRight: '3px',
                            // display: 'inline-block',
                            minWidth: '20px',
                            height: '20px',
                            borderRadius: '250px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            color: '#fff',
                            lineHeight: '20px',
                            textAlign: 'center',
                            background: 'var(--AnteTitleColor)'
                        }}
                    >
                        {btn.countValue}
                    </span>
                )}
                {/* {btn.countValue === 0 && (
                    <span
                        className="dx-button-text"
                        style={{
                            marginLeft: '6px',
                            paddingLeft: '3px',
                            paddingRight: '3px',
                            display: 'inline-block',
                            minWidth: '20px',
                            height: '20px',
                            borderRadius: '250px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            lineHeight: '20px',
                            textAlign: 'center',
                            color: '#000',
                            background: 'transparent'
                        }}
                    >
                        {btn.countValue}
                    </span>
                )} */}
            </div>
        );
    }, []);

    const renderBatchActions = useCallback(
        (item) => {
            const longestActionText = (item.actionItems instanceof Array ? item.actionItems : []).reduce(
                (length, action) => Math.max(length, String(action.text || '').length),
                0
            );
            // Icon + side paddings + a conservative average character width. Keep the
            // menu readable for translated labels without letting one long label cover the grid.
            const menuWidth = Math.min(420, Math.max(280, 92 + longestActionText * 8));

            return (
                <DropDownButton
                text={AntLocalization.translateText({ vi: 'Thao tác', en: 'Actions', fr: 'Actions', es: 'Acciones', id: 'Tindakan', ja: '操作', zh: '操作', ko: '작업', hi: 'कार्रवाई', ar: 'إجراءات' })}
                hint={AntLocalization.translateText({ vi: 'Thao tác trên các dòng đã chọn', en: 'Actions for selected rows', fr: 'Actions sur les lignes sélectionnées', es: 'Acciones para filas seleccionadas', id: 'Tindakan untuk baris terpilih', ja: '選択した行への操作', zh: '对所选行的操作', ko: '선택한 행에 대한 작업', hi: 'चयनित पंक्तियों पर कार्रवाई', ar: 'إجراءات على الصفوف المحددة' })}
                items={item.actionItems}
                keyExpr="id"
                displayExpr="text"
                showArrowIcon={true}
                stylingMode="text"
                focusStateEnabled={props.focusStateEnabled}
                dropDownOptions={{ width: menuWidth }}
                itemRender={(action) => (
                    <div className={'ant-grid-batch-action-menu-item' + (action.disabled ? ' disabled' : '')} title={action.hint}>
                        {action.icon && <i className={`dx-icon dx-icon-${action.icon}`} />}
                        <span>{action.text}</span>
                    </div>
                )}
                onItemClick={(e) => {
                    const action = e.itemData;
                    if (!action || action.disabled) return;
                    if (props.onBatchAction) props.onBatchAction({ name: action.id, action });
                }}
                />
            );
        },
        [props.focusStateEnabled, props.onBatchAction]
    );

    const getSearchConditions = useCallback(() => {
        var obj = {};

        for (let i = 0; i < mainItems.length; i++) {
            let item = mainItems[i];
            if (item.name !== undefined && item.widget === 'dxCheckBox' && (item.searchable !== undefined ? item.searchable : true)) {
                if (mainItemsValue[item.name] !== undefined) obj[item.name] = mainItemsValue[item.name];
            }
        }
        return obj;
    }, [mainItems, mainItemsValue]);

    const getFilteredGroup = useCallback(() => {
        return filteredByGroups instanceof Array && filteredByGroups.length > 0 ? filteredByGroups[0] : null;
    }, [filteredByGroups]);

    const getFilteredGroupConditions = useCallback(() => {
        var obj = {};

        if (filteredByGroups instanceof Array && filteredByGroups.length > 0) {
            for (let i = 0; i < filteredByGroups.length; i++) {
                const group = filteredByGroups[i];
                if (group.searchConditions)
                    Object.keys(group.searchConditions).forEach((key) => {
                        obj[key] = group.searchConditions[key];
                    });
            }
        }
        return obj;
    }, [filteredByGroups]);

    const runCounting = useCallback(async () => {
        if (props.showActions === false || props.items instanceof Array === false || !props.dataSource) return;

        var group = props.items.find((i) => i.widget === 'ButtonGroup' && i.items instanceof Array && i.items.length > 0);
        if (!group) return;

        var counts = [];
        for (let j = 0; j < group.items.length; j++) {
            let child = group.items[j];

            if (child.searchConditions)
                counts.push({
                    name: child.name,
                    cond: child.searchConditions
                });
        }

        var values = [];

        var result = await props.dataSource.callCountsAPI(counts, {
            byPassCheckAllowQuery: true
        });
        if (result) {
            for (let j = 0; j < group.items.length; j++) {
                let child = JSON.parse(stringify(group.items[j]));
                var found = result.find((r) => r.name === child.name);
                if (found)
                    values.push({
                        name: child.name,
                        value: found.count
                    });
            }
        }

        setCountValues(values);
    }, [props.showActions, props.items, props.dataSource, setCountValues]);

    const resetItems = useCallback((args) => {
        if (!args || Object.keys(args).length <= 0) return;
        if (args.filterBySelected !== undefined) setFilterBySelected(Boolean(args.filterBySelected));
        const customArgs = { ...args };
        delete customArgs.filterBySelected;
        if (Object.keys(customArgs).length > 0) setCustomOpts((current) => ({ ...(current || {}), ...customArgs }));
    }, []);

    const mainBar = useRef();

    useImperativeHandle(ref, () => ({
        getSearchConditions: getSearchConditions,
        getFilteredGroup: getFilteredGroup,
        resetItems: resetItems,
        runCounting: runCounting,
        focusInScanBarcode: focusInScanBarcode,
        focusInSearchBar: focusInSearchBar
    }));

    useEffect(() => {
        if (opts && opts.showFilterBySelected) {
            if (props.onAction) props.onAction({ name: 'filterBySelected', value: filterBySelected });
            if (props.onFilterBySelected) props.onFilterBySelected(filterBySelected);
        }
    }, [filterBySelected]);

    useEffect(() => {
        if (previousSearchProp.current !== props.searchText && props.searchText !== undefined) setSearchText(props.searchText ?? '');
        previousSearchProp.current = props.searchText;
    }, [props.searchText]);

    useEffect(() => {
        if (!showSearchBar || props.visible === false || searchText === null) return undefined;
        searchTextTimeOut.current = setTimeout(() => {
            if (latestSearchCallback.current) latestSearchCallback.current(searchText);
        }, 1000);
        return () => clearTimeout(searchTextTimeOut.current);
    }, [searchText, showSearchBar, props.visible]);

    useEffect(() => {
        return () => {
            clearTimeout(searchFocusTimer.current);
            clearTimeout(barcodeFocusTimer.current);
            if (searchTextTimeOut.current) clearTimeout(searchTextTimeOut.current);
            delete searchTextFocused.current;
            delete searchTextComp.current;
            delete barcodeFocusing.current;
            delete scanBarcodeComp.current;
            delete mainBar.current;

            searchTextFocused.current = null;
            searchTextComp.current = null;
            barcodeFocusing.current = null;
            scanBarcodeComp.current = null;
            mainBar.current = null;
        };
    }, []);

    // Keep the editor as a React child: changing a Toolbar items array disposes its input.
    const searchBar = (
        showSearchBar && (
            <div
                className={props.gridBarSearchClass || 'ant-grid-bar-search'}
                style={{ '--ant-grid-search-width': `${opts.searchBarWidth}px` }}
            >
                <TextBox
                    mode="search"
                    showClearButton={true}
                    placeholder={AntLocalization.translateText({ vi: 'Tìm kiếm...', en: 'Searching...', fr: 'Recherche...', es: 'Buscando...', id: 'Mencari...', ja: '検索中...', zh: '搜索中...', ko: '검색 중...', hi: 'खोज रहा है...', ar: 'جاري البحث...' })}
                    width={opts.searchBarWidth}
                    value={searchText ?? ''}
                    valueChangeEvent="input"
                    onValueChanged={onSearchValueChanged}
                    onInitialized={onSearchInitialized}
                    onDisposing={onSearchDisposing}
                    onFocusIn={onSearchFocusIn}
                    onFocusOut={onSearchFocusOut}
                    focusStateEnabled={props.focusStateEnabled}
                    disabled={props.disabled}
                />
            </div>
        )
    );

    const downloadButton = opts.showDownload && !scanBarcodeError && (
        <div className="ant-grid-bar-download">
            <Button
                icon="download"
                stylingMode="text"
                focusStateEnabled={props.focusStateEnabled}
                hint={AntLocalization.translateText({ vi: 'Tải xuống', en: 'Download', fr: 'Télécharger', es: 'Descargar', id: 'Unduh', ja: 'ダウンロード', zh: '下载', ko: '다운로드', hi: 'डाउनलोड', ar: 'تنزيل' })}
                onClick={() => {
                    if (props.onDownloadClick) props.onDownloadClick();
                }}
            />
        </div>
    );

    // Bộ lọc ngày chỉ hiện khi AntGrid xác định được cột ngày (created_at hoặc dateFilterField).
    const dateFilter = props.dateFilterField && !filterBySelected && (
        <AntGridDateFilter value={props.dateFilterValue} onChange={props.onDateFilterChanged} disabled={props.disabled} />
    );

    // Ẩn hiện cột: AntGrid cấp danh sách cột + xử lý (lưu trong cấu hình lưới).
    const columnChooser = opts.showFieldChooser && props.getColumnChooserItems && (
        <AntGridColumnChooser
            getItems={props.getColumnChooserItems}
            onToggle={props.onColumnVisibilityChange}
            onReset={props.onColumnVisibilityReset}
            onReorder={props.onColumnOrderChange}
        />
    );

    // Lọc trên từng cột: nút ngay trước bộ lọc ngày.
    const columnFilterToggle = props.showColumnFilter && props.onToggleColumnFilter && (
        <ColumnFilterToggle active={props.columnFilterActive} onToggle={props.onToggleColumnFilter} focusStateEnabled={props.focusStateEnabled} />
    );

    const searchTools = (
        <React.Fragment>
            {columnChooser}
            {downloadButton}
            {columnFilterToggle}
            {dateFilter}
            {searchBar}
        </React.Fragment>
    );

    return (
        <React.Fragment>
            <div
                style={{
                    display: props.visible !== false ? 'flex' : 'none'
                }}
            >
                {opts.locationSearchBar !== 'before' && (
                    <div
                        style={{
                            display: 'flex',
                            flex: 1
                        }}
                    >
                        {mainItems.length > 0 && (
                            <Toolbar
                                className="ant-grid-bar"
                                ref={mainBar}
                                items={mainItems}
                                focusStateEnabled={props.focusStateEnabled}
                                disabled={props.disabled}
                            >
                                <Template name={'renderButtonOnGroup'} render={renderButtonOnGroup} />
                                <Template name={'renderBatchActions'} render={renderBatchActions} />
                            </Toolbar>
                        )}
                        {scanBarcodeError && (
                            <div
                                style={{
                                    display: 'flex',
                                    marginRight: '20px',
                                    flex: 1
                                }}
                            >
                                <Box direction="row" height="100%" width="100%">
                                    {/* <BoxItem baseSize={40}>
                                        <Button
                                            className="ant-grid-icon ant-icon-error"
                                            // id={errorId}
                                            type="normal"
                                            stylingMode="text"
                                            focusStateEnabled={false}
                                            hoverStateEnabled={false}
                                            activeStateEnabled={false}
                                        >
                                            <i className={'dx-icon dx-icon-info'}></i>
                                        </Button>
                                    </BoxItem> */}

                                    <BoxItem ratio={1}>
                                        <span className="ant-error-text">{scanBarcodeError}</span>
                                    </BoxItem>
                                </Box>
                            </div>
                        )}

                        {batchActionItems.length > 0 && <div className="ant-grid-bar-batch-actions">{renderBatchActions({ actionItems: batchActionItems })}</div>}
                        {searchTools}
                    </div>
                )}
                {opts.locationSearchBar === 'before' && (
                    <div
                        style={{
                            display: 'flex',
                            flex: 1
                        }}
                    >
                        {batchActionItems.length > 0 && <div className="ant-grid-bar-batch-actions">{renderBatchActions({ actionItems: batchActionItems })}</div>}
                        {searchTools}
                        {mainItems.length > 0 && (
                            <Toolbar
                                className="ant-grid-bar ant-grid-bar-right"
                                ref={mainBar}
                                items={mainItems}
                                focusStateEnabled={props.focusStateEnabled}
                                disabled={props.disabled}
                            >
                                <Template name={'renderButtonOnGroup'} render={renderButtonOnGroup} />
                                <Template name={'renderBatchActions'} render={renderBatchActions} />
                            </Toolbar>
                        )}
                    </div>
                )}
                {mainItems.length <= 0 && <p />}
            </div>
        </React.Fragment>
    );
}));
